import type { Notification } from './types'

/**
 * Turns a notification row into the sentence and destination the UI shows.
 *
 * The API stores a type and a small payload rather than prose, so wording
 * lives here in one place and an unknown type from a newer server still
 * renders as something sensible rather than an empty row.
 */
export interface NotificationDescription {
  /** Who did it, for the leading bold name. */
  actor: string
  /** The sentence after the actor, e.g. "assigned TSK-12 to you". */
  action: string
  /** The issue title, chat preview, or nothing. */
  detail: string | null
  /** Path inside the workspace, or null when there is nowhere to go. */
  path: string | null
}

export function describeNotification(n: Notification): NotificationDescription {
  const p = n.payload ?? {}
  const actor = n.actor?.name ?? p.actor ?? 'Someone'
  const key = p.issue_key ?? ''
  const issuePath = key ? `/issues/${encodeURIComponent(key)}` : null
  const chatPath = p.channel_id ? `/chat/${encodeURIComponent(p.channel_id)}` : '/chat'

  switch (n.type) {
    case 'issue.assigned':
      return { actor, action: `assigned ${key || 'an issue'} to you`, detail: p.issue_title ?? null, path: issuePath }
    case 'issue.transitioned':
      return {
        actor,
        action: `moved ${key || 'an issue'} to ${statusLabel(p.to)}`,
        detail: p.issue_title ?? null,
        path: issuePath,
      }
    case 'comment.mentioned':
      return { actor, action: `mentioned you on ${key || 'an issue'}`, detail: p.preview ?? p.issue_title ?? null, path: issuePath }
    case 'comment.added':
      return { actor, action: `commented on ${key || 'an issue'}`, detail: p.preview ?? p.issue_title ?? null, path: issuePath }
    case 'chat.message':
      return { actor, action: 'sent you a message', detail: p.preview ?? null, path: chatPath }
    case 'chat.mentioned':
      return {
        actor,
        action: `mentioned you in ${p.channel_name ? `#${p.channel_name}` : 'a group'}`,
        detail: p.preview ?? null,
        path: chatPath,
      }
    default:
      return { actor, action: n.type.replace(/[._]/g, ' '), detail: p.issue_title ?? p.preview ?? null, path: issuePath ?? (p.channel_id ? chatPath : null) }
  }
}

function statusLabel(status: string | undefined): string {
  switch (status) {
    case 'pending':
      return 'To Do'
    case 'in_progress':
      return 'In Progress'
    case 'done':
      return 'Done'
    default:
      return status ?? 'another status'
  }
}
